import { z } from 'zod';
import { ContractOfferSchema } from './contracts.js';
/**
 * Phase 22 — Shared Bid Board.
 *
 * The lore promised a "shared universe" where megacorps fight for the same
 * orders. Phases 4 (personal contracts), 4A (market dynamics), and 12
 * (AI charters) each filled a slice; this phase is the missing
 * connector — a *single* pool of premium contract offers that every
 * connected human charter AND every AI charter can race to claim.
 *
 * Model: race-to-claim, not auction. First charter to issue
 * `acceptSharedContract` wins; everyone else sees the offer vanish with
 * a "captured by Helion Forge Cooperative" line in the recent-acceptance
 * feed. AI charters consider each open offer probabilistically per
 * pool-tick (rates per archetype below); humans see the same offers
 * live in the Bid Board panel.
 *
 * Contracts that go to the shared pool are **premium**: payout 1.6× and
 * deadline 0.7× the regular contract pool's offer for the same goal.
 * Worth winning a race for.
 *
 * Personal pool (P4) is unchanged — it remains per-charter. New players
 * still get tutorial-grade offers without competing.
 */
// ── Pool sizing ─────────────────────────────────────────────────────────────
/**
 * Target pool size — the registry tries to keep the shared pool topped
 * up to this many open offers. New offers are generated when the pool
 * dips below the target.
 */
export const SHARED_POOL_TARGET_SIZE = 8;
/**
 * Hard cap on pool size. Even if the refresh tick fires repeatedly,
 * the pool never exceeds this; prevents runaway growth if AI accept
 * rates fall behind generation.
 */
export const SHARED_POOL_MAX_SIZE = 12;
/**
 * Ticks an offer lives in the pool before being evicted as expired.
 * 1800 ticks @ 5 Hz = 6 minutes of wall-clock visibility. Long enough
 * for an idle player to glance at the board, short enough that stale
 * offers don't crowd out fresh ones.
 *
 * Used by race-mode offers (default). Auction-mode offers run on the
 * shorter `AUCTION_OFFER_TTL_TICKS` window below — sealed-bid auctions
 * settle to the highest bid at expiry, so a 6-minute window made the
 * "wait for someone else to outbid you" feedback loop feel sluggish.
 */
export const SHARED_OFFER_TTL_TICKS = 1800;
/**
 * P30.2 — Auction-mode offers settle when this many ticks have elapsed
 * since they were posted. 600 ticks @ 5 Hz = 2 minutes — short enough
 * that the player doesn't have to wait long to see the outcome of a
 * bid, long enough to give competing bidders a chance to raise.
 */
export const AUCTION_OFFER_TTL_TICKS = 600;
/**
 * P31.4 — Bid grace window. Both the bid handler and the settle pass
 * use `offerExpiresTick + AUCTION_BID_GRACE_TICKS` as the actual close
 * threshold; the displayed countdown still reaches 0 at
 * `offerExpiresTick`, but a click that the player launched while the
 * UI showed `0:01` survives the round-trip + tick lag and still lands
 * on the server. Bids that arrive AFTER the grace window are rejected
 * with AUCTION_CLOSED. Settle runs once `currentTick` crosses the
 * grace, so a late-arriving bid is in the book before the winner is
 * picked.
 *
 * 10 ticks @ 5 Hz = 2 seconds — covers ordinary WS latency (100-300ms
 * round-trip) plus the per-tick broadcast cadence (200ms) without
 * making the displayed timer feel deceptive.
 */
export const AUCTION_BID_GRACE_TICKS = 10;
/**
 * How often the bid-board registry runs its full tick (refresh + AI
 * acceptance pass). Server runs at 5 Hz; pool-tick every 30 sim-ticks
 * = ~6 pool-ticks per minute. AI accept rates below are scaled around
 * this cadence.
 */
export const POOL_TICK_INTERVAL_TICKS = 30;
/**
 * Premium multipliers applied on top of P4's base contract math when
 * synthesizing a shared offer. Bigger payout, tighter deadline.
 */
export const PREMIUM_PAYOUT_MULT = 1.6;
export const PREMIUM_DURATION_MULT = 0.7;
/**
 * P22 v3 — Probability that a freshly-spawned bid-board offer is an
 * auction (sealed-bid) instead of the default race-to-claim. ~25% so
 * auctions feel like a recurring "premium event" without dominating the
 * board. Race offers stay the default behaviour for all existing UI +
 * AI accept paths.
 */
export const AUCTION_OFFER_RATIO = 0.25;
/**
 * P22 v3 — Minimum-bid floor as a fraction of the offer's payout.
 * Auction `minBid` is rounded up to a whole credit. Tuned so even a
 * winning bid (which is destroyed as a sink) leaves the player with the
 * majority of the payout as profit — auctions feel rewarding, not
 * punishing. 5% means a ₡20,000 auction has minBid ₡1,000.
 */
export const AUCTION_MIN_BID_FRACTION = 0.05;
/**
 * P22 v3 — Smallest delta between a player's previous bid and their new
 * bid (when re-bidding to outdo their own private bid in case they
 * didn't post enough the first time). Also the smallest accepted bid
 * over and above `minBid` for the first bid. Keeps bid spam off the
 * wire and forces "meaningful" raises.
 */
export const AUCTION_BID_INCREMENT = 50;
/**
 * Maximum simultaneous shared contracts an AI charter can hold. Keeps
 * one well-funded bot from sweeping every offer the moment it lands.
 * Humans use the regular MAX_ACTIVE_CONTRACTS gate from contracts.ts.
 */
export const AI_MAX_CONCURRENT_SHARED_CONTRACTS = 2;
/**
 * Rolling buffer of last N acceptances surfaced to all clients. Used
 * by the BidBoard panel's "Recent captures" feed so players can see
 * who's been winning lately even if they weren't watching live.
 */
export const RECENT_ACCEPTANCES_LIMIT = 12;
// ── Offer (extends ContractOffer with a `postedTick` so we can age) ─────────
/**
 * P22 v3 — Bid-board offer mode.
 *
 *   - `race`: classic first-to-accept wins. The default for all
 *     pre-P22 v3 logic and all AI accept paths.
 *   - `auction`: sealed-bid. Players submit private bids until
 *     `offerExpiresTick`; highest bid wins, losers are refunded, the
 *     winning bid is destroyed (credit sink). AI charters skip
 *     auctions in v1 (humans only).
 */
export const SharedContractOfferModeSchema = z.union([
    z.literal('race'),
    z.literal('auction'),
]);
export const SharedContractOfferSchema = ContractOfferSchema.extend({
    /** Absolute server tick when this offer was added to the pool. */
    postedTick: z.number().int().nonnegative(),
    /**
     * P22 v3 — Mode. Optional for legacy compat; treat `undefined` as
     * `race` on read.
     */
    mode: SharedContractOfferModeSchema.optional(),
    /**
     * P22 v3 — Minimum bid (auction-mode only). Public, broadcast to all
     * connected clients so they know the floor. The actual bids are
     * sealed.
     */
    minBid: z.number().nonnegative().optional(),
});
// ── Acceptance (publicly visible "captured by …" event) ─────────────────────
/**
 * Whoever wins gets identified by a public-facing reference. Users get
 * their corp name; bots get their generated corp name (from P12). We
 * deliberately don't expose userId/botId on the wire — corpName +
 * a `kind` discriminator is enough for the UI.
 */
export const CharterRefSchema = z.object({
    kind: z.union([z.literal('human'), z.literal('ai')]),
    corpName: z.string(),
    /** Optional emblem the client can render next to the name. Humans
     *  carry their P3B emblem; bots use a default badge per archetype. */
    emblemTone: z.string().optional(),
});
export const SharedContractAcceptanceSchema = z.object({
    offerId: z.string(),
    /** Snapshot of the offer at acceptance time so the feed line can
     *  render even after the offer itself is gone from the pool. */
    issuer: z.string(), // FactionId
    resourceId: z.string(),
    quantity: z.number().int().positive(),
    payout: z.number().nonnegative(),
    acceptedBy: CharterRefSchema,
    acceptedTick: z.number().int().nonnegative(),
});
// ── Auction settlement (public) + your-bid summary (private) ────────────────
/**
 * P22 v3 — Public settlement record. When an auction closes, one entry
 * lands here; the rolling buffer is shared with all clients (mirrors
 * `recentAcceptances`). Loser bid amounts are NOT included (sealed-bid
 * privacy).
 */
export const SharedContractAuctionSettlementSchema = z.object({
    offerId: z.string(),
    issuer: z.string(),
    resourceId: z.string(),
    quantity: z.number().int().positive(),
    payout: z.number().nonnegative(),
    /** `null` means the auction expired with no bids. */
    winner: CharterRefSchema.nullable(),
    winningBid: z.number().nonnegative().nullable(),
    settledTick: z.number().int().nonnegative(),
});
/**
 * P22 v3 — Your private bid on a live auction. The server filters this
 * list per-session before broadcast so charterA can't see charterB's
 * bid. Includes the previously-bid amount the player has currently
 * escrowed; UI shows it under the offer's bid input.
 */
export const SharedContractYourBidSchema = z.object({
    offerId: z.string(),
    bid: z.number().positive(),
});
// ── Snapshot envelope sent in worldSnapshot ─────────────────────────────────
export const BidBoardSnapshotSchema = z.object({
    offers: z.array(SharedContractOfferSchema),
    recentAcceptances: z.array(SharedContractAcceptanceSchema),
    /** P22 v3 — public settlement feed for closed auctions. */
    recentAuctionSettlements: z.array(SharedContractAuctionSettlementSchema).optional(),
    /** P22 v3 — per-session private bid summary. Server fills only the
     *  receiving user's entries; clients never see other users' bids. */
    yourBids: z.array(SharedContractYourBidSchema).optional(),
});
// ── AI archetype acceptance heuristics ──────────────────────────────────────
/**
 * Per-archetype probability that an AI charter considers accepting any
 * given open offer on a pool-tick. These are **per offer per tick**, so
 * a pool of 8 offers gives a tech-rusher (0.5%) ≈ 4% per tick total
 * chance of grabbing something. Combined with the pool tick cadence
 * (~6/min), each archetype's expected accept rate works out to:
 *
 *   aggressive-trader:  1.2% × 8 × 6 = ~58% per minute (very active)
 *   specialist:         1.0% × 8 × 6 = ~48% (when there's a signature match)
 *   steady-builder:     0.6% × 8 × 6 = ~29%
 *   tech-rusher:        0.5% × 8 × 6 = ~24% (and only on RP-paying contracts)
 *
 * These are upper bounds; individual archetypes filter the offer list
 * (tech-rusher only considers RP contracts, specialist boosts on
 * signature-pool match) so actual accept rates are lower in practice.
 *
 * Single source of truth — the registry imports this for the per-tick
 * accept roll, tests pin it, balance tweaks live here.
 */
export const AI_ACCEPT_RATES = {
    'aggressive-trader': 0.012,
    'steady-builder': 0.006,
    'tech-rusher': 0.005,
    specialist: 0.010,
};
/**
 * Specialist archetype gets a 5× multiplier on signature-pool matches
 * and a 5× nerf off-signature. So a Helion specialist sees a fuels
 * contract at 5%, but a metals contract at 0.2%. Same total expected
 * activity, sharper preference.
 */
export const SPECIALIST_SIGNATURE_BOOST = 5;
export const SPECIALIST_OFF_SIGNATURE_PENALTY = 0.2;
//# sourceMappingURL=bidBoard.js.map